import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { getApiBaseUrl } from "../utils/apiConfig";
import configService from "../utils/configService";

const AcceptInvite = () => {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const token = query.get("token");

  // Fetch configuration on component mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const clientId = await configService.getGoogleClientId();
        setGoogleClientId(clientId);
      } catch (error) {
        console.error('Failed to load configuration:', error);
        setError('Failed to load application configuration');
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, []);

  useEffect(() => {
    const acceptInvite = async () => {
      try {
        const apiUrl = getApiBaseUrl();
        const response = await axios.get(
          `${apiUrl}/organization/accept-invite`,
          {
            params: { token },
          }
        );

        if (response.data.message === "User already exists") {
          // Redirect to login or dashboard
          const user = response.data.user;
          if (localStorage.getItem("token")) {
            navigate("/dashboard", {
              state: {
                message: `You've successfully joined ${
                  user.organizations[user.organizations.length - 1].name
                }!`,
              },
            });
          } else {
            navigate("/login", {
              state: {
                message: `You've successfully joined ${
                  user.organizations[user.organizations.length - 1].name
                }!`,
              },
            });
          }
        } else if (response.data.message === "New user") {
          // Set state for new user
          setEmail(response.data.email);
          setOrganizationId(response.data.organizationId);
          setIsNewUser(true);
        }
      } catch (err) {
        console.error(err);
        setMessage("Invalid or expired token");
      }
    };

    acceptInvite();
  }, [navigate, token]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const username = email.split("@")[0];
      const apiUrl = getApiBaseUrl();
      const response = await axios.post(
        `${apiUrl}/auth/signup`,
        {
          username,
          email,
          password,
          organizationId,
        }
      );
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      navigate("/dashboard", {
        state: {
          message: `You've successfully joined ${response.data.organization.name}!`,
        },
      });
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          "User already exists" ||
          "Error creating account"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (response) => {
    setLoading(true);
    try {
      const { credential } = response;
      const apiUrl = getApiBaseUrl();
      const res = await axios.post(
        `${apiUrl}/auth/google-signup`,
        { tokenId: credential, organizationId }
      );
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      navigate("/dashboard", {
        state: {
          message: `You've successfully joined ${res.data.organization.name}!`,
        },
      });
    } catch (err) {
      console.error(err);
      setError("Error signing up with Google");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleFailure = (response) => {
    console.error(response);
    setError("Google sign up failed");
  };

  // Show loading state while configuration is being fetched
  if (configLoading || !googleClientId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-gradient-dash">
        <div className="w-full max-w-sm p-6 flex flex-col gap-10">
          <div className="text-center">
            <div className="inline-block align-middle w-6 h-6 border-2 border-t-2 border-t-brand border-grey-30 rounded-full animate-spin"></div>
            <p className="mt-2 text-sm text-grey-60">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <div className="min-h-screen flex items-center justify-center bg-bg-gradient-dash">
        <div className="w-full max-w-sm p-6 flex flex-col gap-10">
          <h2 className="text-xl  font-normal text-black-0">
            Accept Invitation
          </h2>
          {message && <p>{message}</p>}
          {isNewUser && (
            <div className="flex flex-col gap-7">
              <p className="text-black-0">
                You are creating an account with this email:{" "}
                <strong>{email}</strong>
              </p>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleFailure}
                render={(renderProps) => (
                  <button
                    onClick={renderProps.onClick}
                    disabled={renderProps.disabled || loading}
                    className="w-full py-2 px-4 bg-brand text-white-f text-sm  font-medium tracking-wide rounded-md hover:bg-brand-dark transition"
                  >
                    {loading ? (
                      <div className="inline-block align-middle w-4 h-4 border-2 border-t-2 border-t-white-f border-bg-opacity rounded-full animate-spin"></div>
                    ) : (
                      "Sign up with Google"
                    )}
                  </button>
                )}
              />
              <form onSubmit={handleSignup} className="flex flex-col gap-7">
                <div className="flex flex-col gap-1">
                  <label className="text-grey-60 text-sm  font-medium">
                    Password
                  </label>
                  <input
                    className="w-full px-3 py-1 border border-grey-30 rounded-md focus:outline-none"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <button
                  className="w-full py-2 px-4 bg-brand text-white-f text-sm  font-medium tracking-wide rounded-md hover:bg-brand-dark transition"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="inline-block align-middle w-4 h-4 border-2 border-t-2 border-t-white-f border-bg-opacity rounded-full animate-spin"></div>
                  ) : (
                    "Sign Up"
                  )}
                </button>
                {error && (
                  <p className="text-red-strong text-sm  font-normal">
                    {error}
                  </p>
                )}
              </form>
            </div>
          )}
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default AcceptInvite;
